import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import {
  IconButton,
  Paper,
  Switch as MaterialSwitch,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import { AxiosResponse, CancelTokenSource, Method } from 'axios';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { OptionsObject, SnackbarKey } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import {
  SetterOrUpdater,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
} from 'recoil';

import { IUserAtom, user } from '../../recoil/atoms/user';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { DeleteSheduledReportModal } from '../components/modals/DeleteSheduledReportModal';
import ScheduledExportModal from '../components/modals/ScheduledExportModal';
import {
  IAlertWithProgressBar,
  useSnackbarWithStyles,
} from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  idColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '80px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  switchColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '100px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameColumn: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: '250px !important',
    width: '100% !important',
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'row',
    // alignItems: 'center',
    justifyContent: 'center',
    // width: '100%'
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
}));
export interface Email {
  email: string;
}
export interface IScheduledReport {
  scheduledReportId: number;
  reportType: 'scheduled';
  businessId: number;
  active: 0 | 1;
  name: string;
  kixieReportName: 'agent-summary' | 'call-history' | 'sms-history';
  frequency: 'daily' | 'weekly' | 'monthly';
  lastExportDate?: Date | string;
  lastExportURL?: string;
  lastExportStatus?: 'completed' | 'failed';
  nextExportDate: Date | string;
  emailList: string[];
  createdByUserId: number;
}

export interface IScheduledReportRow {
  key: number;
  id: number;
  active: 0 | 1;
  name: string;
  kixieReportName: 'agent-summary' | 'call-history' | 'sms-history';
  frequency: 'daily' | 'weekly' | 'monthly';
  lastExportDate?: Date | string;
  lastExportURL?: string;
  lastExportStatus?: 'completed' | 'failed';
  nextExportDate: Date | string;
  emailList: string[];
}

const ScheduledReportToggle = (props: {
  params: any;
  mutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    {
      endpoint: string;
      payload?:
        | {
            [key: string]: any;
          }
        | undefined;
      method?: Method | undefined;
      omitBusinessIdAndApikey?: boolean | undefined;
      domain?: string;
      stage?: 'itn' | 'app' | 'auto' | 'crm' | 'utils' | 'tele';
    },
    unknown
  >;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey;
  setRefetchToggleForScheduledReports: SetterOrUpdater<any>;
  reportToEdit: any;
  userAtomContents: IUserAtom;
  disabled?: boolean;
}) => {
  const [checked, setChecked] = useState<boolean>(
    Boolean(props.params?.value === 1)
  );
  useEffect(() => {
    setChecked(Boolean(props?.params?.value === 1));
  }, [props?.params?.value]);
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> => {
    setChecked(checked);
    try {
      const getEditEndpoint = () =>
        props?.userAtomContents.businessid
          ? endpointEnum.UPDATE_SCHEDULED_REPORT.replace(
              '{bid}',
              props?.userAtomContents.businessid.toString()
            ).replace(
              `{scheduledReportId}`,
              props?.reportToEdit?.scheduledReportId?.toString() as string
            )
          : '';

      const data = {
        ...props?.reportToEdit,
        active: checked ? 1 : 0,
        businessId: props?.reportToEdit?.businessId,
        kixieReportName: props?.reportToEdit?.kixieReportName,
        reportType: 'scheduled',
        name: props?.reportToEdit?.name,
        scheduledReportId: props?.reportToEdit?.scheduledReportId,
        emailList: props?.reportToEdit?.emailList
          ? JSON.parse(props?.reportToEdit?.emailList)
          : [],
      } as any;

      const response = await props.mutation.mutateAsync({
        endpoint: getEditEndpoint(),
        payload: data,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'itn',
        method: 'PUT',
      });
      if (response?.status === 200 || response?.status === 201) {
        props?.enqueueSnackbarWithStyles({
          message: `Success updating scheduled report`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (err) {
      props.enqueueSnackbarWithStyles({
        message: `Error updating scheduled report active status`,
        alertProps: { severity: 'error' },
      });
    }
    props?.setRefetchToggleForScheduledReports({});
  };
  return (
    <MaterialSwitch
      checked={checked}
      onChange={handleChange}
      color="primary"
      name="status"
      inputProps={{ 'aria-label': 'primary checkbox' }}
      disabled={props.disabled}
    />
  );
};

const ScheduledReports = () => {
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [editID, setEditID] = useState<number | null>(null);
  const [deleteID, setDeleteID] = useState<number | null>(null);
  const [deleteRow, setDeleteRow] = useState<any>(null);

  const [, setUserAtom] = useRecoilStateLoadable(user);
  const [, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();

  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );

  const getScheduledReportsEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.GET_SCHEDULED_REPORTS.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        )
      : '';

  const reports = [
    {
      name: 'Agent Summary',
      value: 'agent-summary',
    },
    {
      name: 'Call History',
      value: 'call-history',
    },
    {
      name: 'SMS History',
      value: 'sms-history',
    },
    {
      name: 'PowerList Contacts',
      value: 'powerlist-contacts',
    },
  ];

  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [
    _,
    scheduledReportsLoadable,
    setRefetchToggleForScheduledReports,
  ] = makeApiRequestCached(
    {
      endpoint: getScheduledReportsEndpoint(),
      stage: 'itn',
      method: 'GET',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );

  const rows = useMemo<IScheduledReportRow[]>(
    () =>
      scheduledReportsLoadable?.state === 'hasValue'
        ? scheduledReportsLoadable?.contents?.response?.data?.map((el, i) => ({
            key: el?.scheduledReportId,
            id: el?.scheduledReportId,
            active: el?.active,
            name: el?.name,
            report: reports.find((r) => r?.value === el?.kixieReportName)?.name,
            frequency: el?.frequency,
            emailRecipients: JSON.parse(el?.emailList).length,
            lastExportDate:
              el?.lastExportDate !== null
                ? DateTime.fromISO(el?.lastExportDate).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )
                : 'None',
            status: el?.lastExportStatus ?? 'None',
            nextExportDate:
              el?.nextExportDate === null
                ? 'None'
                : DateTime.fromISO(el?.nextExportDate).toLocaleString(
                    DateTime.DATETIME_SHORT
                  ),
          }))
        : [],
    [scheduledReportsLoadable]
  );

  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'nextExportDate', sort: 'desc' },
  ]);

  const getResendEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.RESEND_SCHEDULED_REPORT.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        )
      : '';

  const handleResend = async (reportId, params) => {
    const recipients = scheduledReportsLoadable?.contents?.response?.data?.find(
      (r) => r?.scheduledReportId === reportId
    )?.emailList;
    try {
      const response = await mutation.mutateAsync({
        endpoint: getResendEndpoint(),
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'itn',
        method: 'POST',
        payload: {
          scheduledReportId: reportId,
          apikey: userAtomContents?.apikey,
          businessid: userAtomContents?.businessid,
          businessId: userAtomContents?.businessid,
          To: recipients,
          emailList: !Array.isArray(recipients)
            ? JSON.parse(recipients)
            : recipients,
        },
      });
      if (response?.status !== 200) {
        enqueueSnackbarWithStyles({
          message: `Encountered an error re-sending the scheduled report(s)`,
          alertProps: { severity: 'error' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `${
            response?.data ?? 'Your reports were successfully emailed'
          }`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (err) {
      enqueueSnackbarWithStyles({
        message: `Encountered an error re-sending the scheduled report(s)`,
        alertProps: { severity: 'error' },
      });
    }
  };

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        headerClassName: classes.idColumn,
        cellClassName: classes.idColumn,
      },
      {
        field: 'active',
        headerName: 'Active',
        headerAlign: 'center',
        align: 'center',
        headerClassName: classes.switchColumn,
        cellClassName: classes.switchColumn,
        enableSorting: false,
        renderCell: (params) => {
          const reportToEdit = scheduledReportsLoadable?.contents?.response?.data?.find(
            (r) => r?.scheduledReportId == params.row.id
          );
          return (
            <div className={classes.actionColumn}>
              <ScheduledReportToggle
                params={params}
                mutation={mutation}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                setRefetchToggleForScheduledReports={
                  setRefetchToggleForScheduledReports
                }
                reportToEdit={reportToEdit}
                userAtomContents={userAtomContents}
              />
            </div>
          );
        },
        disableExport: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        headerClassName: classes.nameColumn,
        cellClassName: classes.nameColumn,
      },
      {
        field: 'report',
        headerName: 'Report',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'frequency',
        headerName: 'Frequency',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div>
                {typeof params.value === 'string' &&
                  params.value.charAt(0).toUpperCase() + params.value.slice(1)}
              </div>
            </>
          );
        },
      },
      {
        field: 'emailRecipients',
        headerName: 'Email Recipients',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div>{`${params?.value ?? 0}` + ' Recipients'}</div>
            </>
          );
        },
      },
      {
        field: 'lastExportDate',
        headerName: 'Last Export Date',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'status',
        headerName: 'Status',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'nextExportDate',
        headerName: 'Next Export Date',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'key',
        headerName: 'Actions',
        // hide: readOnly,
        width: 150,
        cellClassName: classes.actionColumn,
        disableExport: true,
        disableSearch: true,
        headerAlign: 'center',
        disableRowSelection: true,
        enableSorting: false,
        renderCell: (params) => (
          <div className={clsx(classes.actionColumn, 'row')}>
            <Tooltip title="Resend last export to recipients">
              <IconButton
                onClick={() => {
                  handleResend(params?.value, params);
                }}
                name="resend"
                size="large"
              >
                <SendIcon
                  style={{
                    transform: 'rotateY(0deg) rotate(-45deg)',
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => {
                  setEditID(params.value as number);
                  setShowExportModal(true);
                }}
                name="edit"
                size="large"
              >
                <CreateIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  const reportToDelete = scheduledReportsLoadable?.contents?.response?.data?.find(
                    (r) => r?.scheduledReportId === params?.value
                  );
                  setDeleteRow(reportToDelete);
                  setDeleteID(params.value as number);
                  setShowDeleteModal(true);
                }}
                data-pup={`delete-scheduled-report`}
                name="delete"
                size="large"
              >
                <DeleteIcon className={classes.deleteIconButton} />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [classes, scheduledReportsLoadable]
  );
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        isLoading={scheduledReportsLoadable.state === 'loading'}
        dataType="ScheduledReports"
        // numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            dataPup={'add-scheduled-report'}
            onClickForAdd={() => {
              setShowExportModal(true);
            }}
            isLoading={scheduledReportsLoadable.state === 'loading'}
            addButtonText={'Add Scheduled Export'}
          />
        }
      />
    ),
    [scheduledReportsLoadable]
  );

  const getDeleteEndpoint = (deleteID) =>
    userAtomContents.businessid
      ? endpointEnum.DELETE_SCHEDULED_REPORT.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        ).replace(`{deleteID}`, deleteID?.toString() as string)
      : '';

  const handleDeleteReport = async (deleteID) => {
    try {
      const response = await mutation.mutateAsync({
        endpoint: getDeleteEndpoint(deleteID),
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'itn',
        method: 'DELETE',
        payload: {
          scheduledReportId: deleteID,
          apikey: userAtomContents?.apikey,
          businessid: userAtomContents?.businessid,
          businessId: userAtomContents?.businessid,
        },
      });
      if (response?.status !== 200) {
        enqueueSnackbarWithStyles({
          message: `Encountered an error deleting the scheduled report`,
          alertProps: { severity: 'error' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: response?.data,
          alertProps: { severity: 'success' },
        });
      }
      setRefetchToggleForScheduledReports({});
    } catch (err) {
      enqueueSnackbarWithStyles({
        message: `Encountered an error deleting the scheduled report`,
        alertProps: { severity: 'error' },
      });
      setRefetchToggleForScheduledReports({});
    }
    setDeleteID(null);
  };

  return (
    <div style={{ maxWidth: '100%' }}>
      <DeleteSheduledReportModal
        deleteId={deleteID}
        handleClose={() => {
          setRefetchToggleForScheduledReports({});
          setShowDeleteModal(false);
        }}
        show={showDeleteModal}
        deleteRow={deleteRow}
        handleDeletion={handleDeleteReport}
      />
      <ScheduledExportModal
        show={showExportModal}
        handleClose={() => {
          setShowExportModal(false);
          setEditID(null);
          setRefetchToggleForScheduledReports({});
        }}
        editID={editID}
        userAtomContents={userAtomContents}
        mutation={mutation}
        reports={reports}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        setRefetchToggleForScheduledReports={
          setRefetchToggleForScheduledReports
        }
      />
      <Paper square={true} elevation={0}>
        <DataTable
          rows={rows ?? []}
          rowCount={rows?.length ?? 0}
          columns={columns}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
          ToolbarComponent={ToolbarComponent}
          loading={scheduledReportsLoadable.state === 'loading'}
        />
      </Paper>
    </div>
  );
};

export default ScheduledReports;
